/** One recommendation returned by the Historic NAR list endpoint. */
export interface HistoricNarRecommendation {
  identifier: string;
  payer?: string;
  line_of_business?: string;
  plan_name?: string;
  j_code?: string;
  medicine_name?: string;
  month?: string;
  score?: number;
  volume_analytics?: {
    calc1?: number;
    calc2?: number;
    calc3?: number;
    source_text?: Record<string, number>;
  } | null;
  grid_match?: HistoricNarGridMatch | null;
  created_at?: string;
  updated_at?: string;
  ingested?: boolean;
  linked_auth_grid_identifier?: string | null;
  status?: string;
  touchless_enabled_for?: string[];
  agentic_enabled_for?: string[];
}

/** Auth Grid result associated with a Historic NAR recommendation. */
export type HistoricNarGridMatch =
  "linked" | "created" | "not_found" | "unmapped";

/** Automation clients submitted when ingesting a recommendation. */
export interface HistoricNarAutomation {
  agent_client_ids: string[];
  touchless_client_ids: string[];
}

/** Request body for one page of Historic NAR recommendations. */
export interface ListHistoricNarRequest {
  payer?: string;
  line_of_business?: string;
  j_code?: string;
  min_score?: number;
  ingested?: boolean;
  include_unmapped?: boolean;
  include_past_months?: boolean;
  limit?: number;
  offset?: number;
}

/** Parsed backend response for one recommendation page. */
export interface ListHistoricNarResponse {
  items: HistoricNarRecommendation[];
  total: number;
  limit: number;
  offset: number;
}

/** Filters accepted when collecting every recommendation page. */
export type ListAllHistoricNarFilters = Omit<
  ListHistoricNarRequest,
  "limit" | "offset"
>;

/** Request body for ingesting or linking a recommendation. */
export interface IngestHistoricNarRequest {
  identifier: string;
  /** Both arrays are required, including when no clients are selected. */
  automation: HistoricNarAutomation;
  source_text: string;
  source_file?: string;
  never_expire?: boolean;
}

/** Parsed backend response for an ingest attempt. */
export interface IngestHistoricNarResponse {
  success: boolean;
  created: boolean;
  linked_auth_grid_identifier?: string | null;
  grid_match: HistoricNarGridMatch;
  message: string;
}

/** Admin reasons accepted when clearing a recommendation from Needs Review. */
export enum HistoricNarClearReason {
  FLIP_WAS_WRONG = "flip_was_wrong",
  ONE_OFF_EXCEPTION = "one_off_exception",
  PAYER_CHANGED_POLICY = "payer_changed_policy",
  DO_NOT_AUTOMATE = "do_not_automate",
}

/** Request body for clearing a Historic NAR Needs Review lane. */
export interface ClearHistoricNarLaneRequest {
  identifier: string;
  reason: HistoricNarClearReason;
  notes?: string;
  order_ids?: string[];
  actor?: string;
}

/** Backend result after clearing a Historic NAR Needs Review lane. */
export interface ClearHistoricNarLaneResponse {
  cleared: boolean;
  state: string;
}
